import { useTranslation } from 'react-i18next';
import { Typography, Card, CardContent, Stack, Box, Chip } from '@mui/material';
import { useCallback } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { showLabel } from '@/utils/helper';
import DingDay21SummaryFilterSection from './components/DingDay21SummaryFilterSection';
import { useGetDingDay21ListQuery } from '@/api/exercise/dingDay21ApiSlice';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { ExerciseSeriesPinnedDTO } from '@/api/DTO/exercise/dingDay21.interface';
import { OnoffType } from '@/api/enum/common.enum';
import AddExerciseDialog from './components/AddExerciseDialog';
import SetOrderDialog from './components/SetOrderDialog';

export interface DataGrid {
	updatedAt: string;
	singleCount: number;
}

export default function DingDay21ListSummaryPage() {
	const { t } = useTranslation();

	const statusList = [
		{ id: OnoffType.off, name: t('meditation.off') },
		{ id: OnoffType.on, name: t('meditation.on') },
	];

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetDingDay21ListQuery(null, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: ExerciseSeriesPinnedDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					singleCount: 21,
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('exerciseSetting.seriesTitleZh'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('exerciseSetting.seriesStatus'),
			flex: 15,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.published, statusList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('exerciseSetting.lastUpdateTime'),
			flex: 21,
		},
		{
			field: 'singleCount',
			headerName: t('exerciseSetting.exerciseCount'),
			flex: 21,
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.exerciseSetting')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<AddExerciseDialog />
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<DingDay21SummaryFilterSection></DingDay21SummaryFilterSection>

					<Box marginTop={2}>
						<LocalDataGrid
							rows={transformResponse(propertySummary?.data) ?? []}
							columns={columns}
							rowCount={0}
							getRowId={(row) => row.id}
							pagination={false}
						/>
					</Box>
				</CardContent>
			</Card>
		</>
	);
}
